import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { chartPalette } from './chartPalette';

interface OrderStatusData {
  estado: string;
  cantidad: number;
}

type DashboardMode = 'light' | 'dark';

interface OrdersStatusChartProps {
  data?: OrderStatusData[];
  loading?: boolean;
  mode?: DashboardMode;
}

const ChartContainer = styled(Paper, { shouldForwardProp: (p) => p !== 'mode' })<{
  mode?: DashboardMode;
}>(({ mode = 'dark' }) => {
  const isLight = mode === 'light';
  return {
    padding: '24px',
    borderRadius: '16px',
    position: 'relative',
    overflow: 'hidden',
    ...(isLight
      ? {
          background: '#ffffff',
          border: '1px solid #bae6fd',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          '&::before': { content: 'none' },
        }
      : {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          background:
            'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }),
  };
});

const HeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  position: 'relative',
  zIndex: 1,
});

const Title = styled(Typography, { shouldForwardProp: (p) => p !== 'mode' })<{
  mode?: DashboardMode;
}>(({ mode = 'dark' }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: mode === 'light' ? '#0f172a' : '#ffffff',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  letterSpacing: '-0.01em',
}));

const STATUS_COLORS: Record<string, string> = {
  Borrador: chartPalette.status.neutral,
  Confirmado: chartPalette.status.inProgress,
  'Pago Parcial': chartPalette.status.warning,
  Pagado: chartPalette.status.success,
  Entregado: chartPalette.accent.teal,
  Cancelado: chartPalette.status.neutral,
  Reembolsado: '#94a3b8',
};

// Custom Tooltip
const CustomTooltip = ({ active, payload, isLight }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const total = payload.reduce((sum: number, item: any) => sum + item.value, 0);
    const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : '0';

    const estadoColor =
      STATUS_COLORS[data.payload.estado as keyof typeof STATUS_COLORS] ||
      data.payload.fill ||
      '#ffffff';

    return (
      <Box
        sx={
          isLight
            ? {
                background: '#ffffff',
                border: '1px solid #e0f2fe',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }
            : {
                background:
                  'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
              }
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Box
            sx={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: estadoColor,
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: estadoColor,
              fontFamily: 'Inter, sans-serif',
              textShadow: isLight ? 'none' : `0 0 8px ${estadoColor}60`,
            }}
          >
            {data.payload.estado}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            color: isLight ? '#0f172a' : '#ffffff',
            fontFamily: 'Inter, sans-serif',
            textShadow: isLight ? 'none' : `0 0 12px ${estadoColor}80`,
            marginTop: '4px',
          }}
        >
          {data.value} {data.value === 1 ? 'venta' : 'ventas'}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.7)',
            fontFamily: 'Inter, sans-serif',
            marginTop: '4px',
          }}
        >
          {percentage}% del total
        </Typography>
      </Box>
    );
  }
  return null;
};

export const OrdersStatusChart: React.FC<OrdersStatusChartProps> = ({
  data,
  loading = false,
  mode = 'dark',
}) => {
  const isLight = mode === 'light';
  const gridStroke = isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.05)';
  const axisStroke = isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)';
  const tickFill = isLight ? '#64748b' : 'rgba(255, 255, 255, 0.6)';
  const labelFill = isLight ? '#1e293b' : '#ffffff';
  const chartData = useMemo(() => data ?? [], [data]);

  const total = chartData.reduce((sum, item) => sum + item.cantidad, 0);

  if (loading) {
    return (
      <ChartContainer mode={mode}>
        <Box
          sx={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            Cargando...
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  if (chartData.length === 0) {
    return (
      <ChartContainer mode={mode}>
        <HeaderContainer>
          <Title mode={mode}>Ventas por estado</Title>
        </HeaderContainer>
        <Box
          sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            No hay datos en el periodo seleccionado
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer mode={mode}>
      <HeaderContainer>
        <Title mode={mode}>Ventas por estado</Title>
      </HeaderContainer>

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis
              dataKey="estado"
              stroke={axisStroke}
              style={{ fontSize: '13px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
            />
            <YAxis
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
            />
            <Tooltip
              content={<CustomTooltip isLight={isLight} />}
              cursor={{ fill: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255, 255, 255, 0.05)' }}
              wrapperStyle={{ outline: 'none' }}
            />
            <Bar
              dataKey="cantidad"
              radius={[8, 8, 0, 0]}
              animationDuration={1500}
              animationEasing="ease-out"
              isAnimationActive={true}
              label={{
                position: 'top',
                fill: labelFill,
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'Inter, sans-serif',
                formatter: (value: number) => `${value}`,
              }}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.estado]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box
        sx={
          isLight
            ? {
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                textAlign: 'center',
                position: 'relative',
                zIndex: 1,
              }
            : {
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                textAlign: 'center',
                position: 'relative',
                zIndex: 1,
              }
        }
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.7)',
            fontFamily: 'Inter, sans-serif',
            marginBottom: '4px',
          }}
        >
          Total de ventas
        </Typography>
        <Typography
          sx={{
            fontSize: '24px',
            fontWeight: 700,
            color: isLight ? '#0f172a' : '#ffffff',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {total.toLocaleString('es-BO')} ventas
        </Typography>
      </Box>
    </ChartContainer>
  );
};
